import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, Search, ListChecks, Boxes as BoxesIcon, FileText, Save, Hash, Calendar, Type, Copy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import LabelTile from "@/components/label/LabelTile";
import {
  FONT_FAMILIES, FONT_SCALES, SIZE_PRESETS, FIELD_OPTS, FONT_SIZE_PRESETS_PX, DEFAULT_FIELD_SIZES_PX,
  loadLabelFormat, saveLabelFormat, resolveFormat,
} from "@/lib/labelFormat";

/**
 * Custom Label Print — one page to build any label batch.
 *
 * Three ways to pick cartons: by Order, by SKU/Category, or a manual list.
 * The format chosen here (size, font, fields, custom text) is persisted and
 * automatically applied to every other label print flow (Quick Weigh, single
 * carton, bulk print buttons).
 *
 * URL: /labels/custom
 */
export default function CustomLabels() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("order");
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [selOrderId, setSelOrderId] = useState("");
  const [selProductId, setSelProductId] = useState("");
  const [manualText, setManualText] = useState("");
  const [candidateCartons, setCandidateCartons] = useState([]);
  const [selectedBoxNos, setSelectedBoxNos] = useState(new Set());
  const [labelData, setLabelData] = useState([]);

  // By-Date filter state
  const todayStr = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(todayStr);
  const [dateTo, setDateTo] = useState(todayStr);
  const [dateSku, setDateSku] = useState("");

  // Text-only mode state: one label per non-empty line.
  const [textLines, setTextLines] = useState("");

  // Copies multiplier — every label in the batch is repeated this many times.
  const [copies, setCopies] = useState(1);

  // Delivery-challan net weight — an ad-hoc value typed for THIS batch only
  // (intentionally not persisted in the saved label format so a stale weight
  // never leaks into the next batch's labels).
  const [dcNetWeight, setDcNetWeight] = useState("");

  // Format state — hydrated from localStorage so returning users see their choice.
  const initial = loadLabelFormat();
  const [size, setSize] = useState(initial.size);
  const [fields, setFields] = useState(new Set(initial.fields));
  const [customText, setCustomText] = useState(initial.customText);
  const [fontFamily, setFontFamily] = useState(initial.fontFamily);
  const [fontScale, setFontScale] = useState(initial.fontScale);
  const [fieldSizes, setFieldSizes] = useState(initial.fieldSizes || {});
  const [savedFlash, setSavedFlash] = useState(false);
  const [busy, setBusy] = useState(false);

  // Carton auto-numbering template (prefix + suffix + digit count). Loaded
  // from backend; changes affect only cartons produced AFTER save.
  const [cartonTpl, setCartonTpl] = useState(null); // {prefix, suffix, seq_digits, current_seq, next_preview}
  const [tplPrefix, setTplPrefix] = useState("");
  const [tplSuffix, setTplSuffix] = useState("");
  const [tplDigits, setTplDigits] = useState(5);
  const [tplSaving, setTplSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/carton-numbering").then((r) => {
      setCartonTpl(r.data);
      setTplPrefix(r.data.prefix || "");
      setTplSuffix(r.data.suffix || "");
      setTplDigits(r.data.seq_digits || 5);
    }).catch(() => setCartonTpl({ error: true }));
  }, []);

  const tplLivePreview = useMemo(() => {
    if (!cartonTpl) return "";
    const seq = (cartonTpl.current_seq || 0) + 1;
    const pad = Math.max(1, Math.min(10, Number(tplDigits) || 5));
    return `${tplPrefix}${String(seq).padStart(pad, "0")}${tplSuffix}`;
  }, [cartonTpl, tplPrefix, tplSuffix, tplDigits]);

  const tplDirty =
    cartonTpl && !cartonTpl.error && (
      tplPrefix !== (cartonTpl.prefix || "") ||
      tplSuffix !== (cartonTpl.suffix || "") ||
      Number(tplDigits) !== (cartonTpl.seq_digits || 5)
    );

  const saveCartonTpl = async () => {
    if (!tplPrefix.trim()) return toast.error("Prefix cannot be empty");
    setTplSaving(true);
    try {
      const r = await api.put("/settings/carton-numbering", {
        prefix: tplPrefix.trim(),
        suffix: tplSuffix,
        seq_digits: Number(tplDigits) || 5,
      });
      setCartonTpl(r.data);
      toast.success(`Saved — next carton will be ${r.data.next_preview}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save carton format");
    } finally { setTplSaving(false); }
  };

  // Persist format whenever any control changes so other pages pick it up.
  useEffect(() => {
    saveLabelFormat({ size, fontFamily, fontScale, fields: [...fields], customText, fieldSizes });
    setSavedFlash(true);
    const t = setTimeout(() => setSavedFlash(false), 900);
    return () => clearTimeout(t);
  }, [size, fontFamily, fontScale, fields, customText, fieldSizes]);

  useEffect(() => {
    api.get("/orders").then((r) => setOrders(r.data || []));
    api.get("/products").then((r) => setProducts(r.data || []));
  }, []);

  // Fetch candidate cartons whenever the mode input changes.
  useEffect(() => {
    (async () => {
      setCandidateCartons([]);
      setSelectedBoxNos(new Set());
      if (mode === "order" && selOrderId) {
        try {
          const r = await api.get(`/orders/${selOrderId}/assigned`);
          setCandidateCartons(r.data.cartons || []);
        } catch { setCandidateCartons([]); }
      } else if (mode === "sku" && selProductId) {
        try {
          const r = await api.get(`/products/${selProductId}/boxes`);
          setCandidateCartons(r.data || []);
        } catch { setCandidateCartons([]); }
      } else if (mode === "date" && dateFrom && dateTo) {
        try {
          const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
          if (dateSku) params.set("product_id", dateSku);
          const r = await api.get(`/cartons/by-date?${params.toString()}`);
          setCandidateCartons(r.data || []);
        } catch { setCandidateCartons([]); }
      }
    })();
  }, [mode, selOrderId, selProductId, dateFrom, dateTo, dateSku]);

  const loadSelected = async () => {
    if (mode === "text") {
      const lines = textLines.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      if (lines.length === 0) return setLabelData([]);
      setLabelData(lines.map((line) => ({ __textOnly: true, __text: line, box_no: null })));
      return;
    }
    let boxes = [];
    if (mode === "manual") {
      boxes = manualText.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean);
    } else {
      boxes = [...selectedBoxNos];
    }
    if (boxes.length === 0) return setLabelData([]);
    setBusy(true);
    try {
      const settled = await Promise.allSettled(
        boxes.map((bn) => api.get(`/cartons/${bn}/activity`).then((r) => r.data)),
      );
      setLabelData(settled.filter((s) => s.status === "fulfilled").map((s) => s.value));
    } finally { setBusy(false); }
  };

  const toggleSelect = (bn) => {
    setSelectedBoxNos((prev) => {
      const next = new Set(prev);
      if (next.has(bn)) next.delete(bn); else next.add(bn);
      return next;
    });
  };
  const toggleAll = () => {
    if (selectedBoxNos.size === candidateCartons.length) setSelectedBoxNos(new Set());
    else setSelectedBoxNos(new Set(candidateCartons.map((c) => c.box_no).filter(Boolean)));
  };

  const { preset, family, scale, fieldSet } = useMemo(
    () => resolveFormat({ size, fontFamily, fontScale, fields: [...fields], customText, fieldSizes }),
    [size, fontFamily, fontScale, fields, customText, fieldSizes],
  );

  const pages = useMemo(() => {
    if (labelData.length === 0) return [];
    // Repeat each label N times per the Copies setting.
    const n = Math.max(1, Math.min(500, Number(copies) || 1));
    const expanded = labelData.flatMap((d) => Array.from({ length: n }, () => d));
    if (preset.perPage === 1) return expanded.map((d) => [d]);
    const chunks = [];
    for (let i = 0; i < expanded.length; i += preset.perPage) {
      chunks.push(expanded.slice(i, i + preset.perPage));
    }
    return chunks;
  }, [labelData, preset.perPage, copies]);

  const totalLabels = useMemo(
    () => labelData.length * Math.max(1, Math.min(500, Number(copies) || 1)),
    [labelData.length, copies],
  );

  const doPrint = () => window.print();

  return (
    <div className="min-h-screen bg-zinc-100 print:bg-white p-4 print:p-0" data-testid="custom-labels-page">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4 print:block">
        {/* ---- Controls ---- */}
        <div className="bg-white border border-zinc-200 p-4 print:hidden space-y-4" data-testid="custom-labels-controls">
          <div>
            <button
              onClick={() => navigate("/orders")}
              data-testid="back-to-app-btn"
              className="mb-2 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-mono border border-zinc-300 px-3 py-1.5 hover:bg-zinc-100"
            >
              <ArrowLeft size={12} /> Back to App
            </button>
            <h1 className="text-lg font-bold">Custom Label Print</h1>
            <p className="text-xs text-zinc-600">
              Pick cartons, choose your format, hit Print.
              <span className="block mt-1 text-[10px] font-mono text-orange-700 flex items-center gap-1">
                <Save size={10} />
                Format below is saved automatically and applied to every label print (Quick Weigh, single, bulk).
                {savedFlash && <span className="text-green-700" data-testid="format-saved-flash">· Saved ✓</span>}
              </span>
            </p>
          </div>

          {/* Mode tabs */}
          <div className="flex gap-1 border-b border-zinc-200 flex-wrap">
            {[
              { k: "order", label: "By Order", Icon: FileText },
              { k: "sku", label: "By SKU", Icon: BoxesIcon },
              { k: "date", label: "By Date", Icon: Calendar },
              { k: "manual", label: "Manual", Icon: ListChecks },
              { k: "text", label: "Text Only", Icon: Type },
            ].map(({ k, label, Icon }) => (
              <button key={k} onClick={() => setMode(k)} data-testid={`mode-${k}`}
                className={`px-3 py-2 text-[11px] uppercase tracking-wider font-mono border-b-2 -mb-px flex items-center gap-1 ${
                  mode === k ? "border-orange-600 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"
                }`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>

          {mode === "order" && (
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">Order</label>
              <select data-testid="order-select" value={selOrderId} onChange={(e) => setSelOrderId(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white">
                <option value="">— pick an order —</option>
                {orders.map((o) => (
                  <option key={o.id} value={o.id}>{o.order_no} · {o.customer_name || ""} · {o.status}</option>
                ))}
              </select>
            </div>
          )}
          {mode === "sku" && (
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">SKU / Category</label>
              <select data-testid="sku-select" value={selProductId} onChange={(e) => setSelProductId(e.target.value)}
                className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white">
                <option value="">— pick a product —</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.sku}</option>
                ))}
              </select>
            </div>
          )}
          {mode === "manual" && (
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">Paste Box Numbers (comma or newline separated)</label>
              <textarea data-testid="manual-text" value={manualText} onChange={(e) => setManualText(e.target.value)}
                rows={5} className="w-full px-2 py-1 border border-zinc-300 text-sm font-mono"
                placeholder="WPL262600006, WPL262600007..." />
            </div>
          )}
          {mode === "date" && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">From</label>
                  <input type="date" data-testid="date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-0.5" />
                </div>
                <div>
                  <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">To</label>
                  <input type="date" data-testid="date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                    className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-0.5" />
                </div>
              </div>
              <div>
                <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">SKU (optional filter)</label>
                <select data-testid="date-sku" value={dateSku} onChange={(e) => setDateSku(e.target.value)}
                  className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-0.5">
                  <option value="">— all SKUs —</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.sku}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {mode === "text" && (
            <div className="space-y-2">
              <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">Text (one label per line)</label>
              <textarea data-testid="text-only-input" value={textLines} onChange={(e) => setTextLines(e.target.value)}
                rows={6} className="w-full px-2 py-1 border border-zinc-300 text-sm font-mono"
                placeholder="FRAGILE&#10;HANDLE WITH CARE&#10;THIS SIDE UP" />
              <div className="text-[9px] font-mono text-zinc-500">
                No carton data — prints just the text on each label. Use with Copies below to make sheets of identical stickers.
              </div>
            </div>
          )}

          {(mode === "order" || mode === "sku" || mode === "date") && candidateCartons.length > 0 && (
            <div className="border border-zinc-200 max-h-56 overflow-y-auto">
              <div className="flex items-center justify-between px-2 py-1.5 bg-zinc-50 border-b border-zinc-200 text-[10px] font-mono uppercase text-zinc-600">
                <button onClick={toggleAll} data-testid="toggle-all-cartons" className="hover:text-orange-700">
                  {selectedBoxNos.size === candidateCartons.length ? "Deselect all" : "Select all"}
                </button>
                <span>{selectedBoxNos.size} / {candidateCartons.length}</span>
              </div>
              {candidateCartons.map((c) => (
                <label key={c.box_no || c.id} className="flex items-center gap-2 px-2 py-1 text-[11px] font-mono border-b border-zinc-100 hover:bg-orange-50 cursor-pointer">
                  <input type="checkbox" data-testid={`carton-${c.box_no}`}
                    checked={selectedBoxNos.has(c.box_no)}
                    onChange={() => toggleSelect(c.box_no)} />
                  <span className="flex-1">{c.box_no}</span>
                  <span className="text-zinc-500">{(c.gross_weight_kg || 0).toFixed(2)} kg</span>
                </label>
              ))}
            </div>
          )}

          <button onClick={loadSelected} data-testid="load-selected-btn" disabled={busy}
            className="w-full bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 text-[11px] uppercase tracking-wider font-mono flex items-center justify-center gap-2 disabled:opacity-50">
            <Search size={12} /> {busy ? "Loading…" : mode === "text" ? "Load text labels" : "Load selected cartons"}
          </button>

          <div>
            <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest flex items-center gap-1"><Copy size={11} /> Copies per label</label>
            <input type="number" min="1" max="500" value={copies}
              data-testid="copies-input"
              onChange={(e) => setCopies(Math.max(1, Math.min(500, Number(e.target.value) || 1)))}
              className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-1 font-mono" />
            <div className="text-[9px] font-mono text-zinc-500 mt-0.5">
              Prints each label {copies}× → {labelData.length} × {copies} = <b>{totalLabels}</b> labels total.
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">Label size</label>
            <select data-testid="size-select" value={size} onChange={(e) => setSize(e.target.value)}
              className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-1">
              {SIZE_PRESETS.map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">Font family</label>
            <select data-testid="font-family-select" value={fontFamily} onChange={(e) => setFontFamily(e.target.value)}
              className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-1">
              {FONT_FAMILIES.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest">Font size</label>
            <select data-testid="font-scale-select" value={fontScale} onChange={(e) => setFontScale(e.target.value)}
              className="w-full h-9 px-2 border border-zinc-300 text-sm bg-white mt-1">
              {FONT_SCALES.map((f) => (
                <option key={f.key} value={f.key}>{f.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-[10px] uppercase font-mono text-zinc-500 tracking-widest block mb-1">Fields to print</label>
            <div className="text-[9px] font-mono text-zinc-500 mb-2">
              Toggle a field on, then tune its font size in px. Empty = auto (scaled by Font size dropdown above).
            </div>
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              {FIELD_OPTS.map((f) => {
                const on = fields.has(f.key);
                const currentPx = fieldSizes[f.key];
                const defaultPx = DEFAULT_FIELD_SIZES_PX[f.key] || 12;
                return (
                  <div key={f.key} className="flex items-center gap-2 px-2 py-1.5 text-[11px]">
                    <label className="flex items-center gap-1.5 flex-1 cursor-pointer">
                      <input type="checkbox" data-testid={`field-${f.key}`}
                        checked={on}
                        onChange={() => setFields((s) => { const n = new Set(s); n.has(f.key) ? n.delete(f.key) : n.add(f.key); return n; })} />
                      {f.label}
                    </label>
                    <select
                      data-testid={`field-size-${f.key}`}
                      disabled={!on}
                      value={currentPx || ""}
                      onChange={(e) => setFieldSizes((prev) => {
                        const n = { ...prev };
                        if (e.target.value === "") delete n[f.key];
                        else n[f.key] = Number(e.target.value);
                        return n;
                      })}
                      className={`h-7 px-1.5 border border-zinc-300 text-[10px] font-mono bg-white ${on ? "" : "opacity-40 cursor-not-allowed"}`}
                      title={`Font size for ${f.label} (default ${defaultPx}px)`}
                    >
                      <option value="">Auto ({defaultPx}px)</option>
                      {FONT_SIZE_PRESETS_PX.map((n) => (
                        <option key={n} value={n}>{n} px</option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            {fields.has("dc_net") && (
              <div className="mt-2 space-y-1">
                <label className="text-[9px] font-mono uppercase tracking-widest text-zinc-500">
                  New Net Weight as per Delivery Challan (kg)
                </label>
                <input value={dcNetWeight} onChange={(e) => setDcNetWeight(e.target.value)}
                  data-testid="dc-net-weight-input"
                  type="number" min="0" step="0.001"
                  className="w-full h-8 px-2 border border-zinc-300 text-xs font-mono"
                  placeholder="e.g. 21.500" />
                <div className="text-[9px] font-mono text-zinc-500">
                  Prints on every label in this batch. Not saved — re-enter for each new batch.
                </div>
              </div>
            )}
            {fields.has("custom") && (
              <input value={customText} onChange={(e) => setCustomText(e.target.value)}
                className="mt-2 w-full h-8 px-2 border border-zinc-300 text-xs" placeholder="Custom text on every label" />
            )}
            {Object.keys(fieldSizes).length > 0 && (
              <button
                type="button"
                data-testid="reset-field-sizes"
                onClick={() => setFieldSizes({})}
                className="mt-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500 hover:text-orange-700 underline"
              >
                Reset all sizes to Auto
              </button>
            )}
          </div>

          {/* --- Carton Number Format (auto-generation for new cartons) --- */}
          <div className="border-2 border-dashed border-orange-300 bg-orange-50/50 p-3 space-y-2" data-testid="carton-numbering-section">
            <div className="flex items-center gap-1.5 text-[10px] uppercase font-mono text-orange-800 tracking-widest">
              <Hash size={11} /> Carton Number Format
            </div>
            <div className="text-[9px] font-mono text-zinc-600">
              Applies to <b>every new carton</b> created after Save. Existing cartons keep their current numbers.
            </div>
            <div className="grid grid-cols-[1fr_1fr_60px] gap-1.5">
              <div>
                <div className="text-[9px] font-mono uppercase text-zinc-500 tracking-wider mb-0.5">Prefix</div>
                <input
                  data-testid="carton-prefix"
                  value={tplPrefix}
                  onChange={(e) => setTplPrefix(e.target.value)}
                  className="w-full h-8 px-2 border border-zinc-300 text-xs font-mono"
                  placeholder="WPL2627"
                />
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase text-zinc-500 tracking-wider mb-0.5">Suffix</div>
                <input
                  data-testid="carton-suffix"
                  value={tplSuffix}
                  onChange={(e) => setTplSuffix(e.target.value)}
                  className="w-full h-8 px-2 border border-zinc-300 text-xs font-mono"
                  placeholder="(optional)"
                />
              </div>
              <div>
                <div className="text-[9px] font-mono uppercase text-zinc-500 tracking-wider mb-0.5">Digits</div>
                <select
                  data-testid="carton-digits"
                  value={tplDigits}
                  onChange={(e) => setTplDigits(Number(e.target.value))}
                  className="w-full h-8 px-1 border border-zinc-300 text-xs font-mono bg-white"
                >
                  {[3, 4, 5, 6, 7, 8].map((d) => (<option key={d} value={d}>{d}</option>))}
                </select>
              </div>
            </div>
            <div className="text-[10px] font-mono text-zinc-700 bg-white border border-zinc-200 px-2 py-1.5">
              Next carton →{" "}
              <span className="font-bold text-orange-800" data-testid="carton-next-preview">
                {cartonTpl?.error ? "—" : (tplLivePreview || "…")}
              </span>
              {cartonTpl && !cartonTpl.error && (
                <span className="ml-2 text-zinc-400">(seq {cartonTpl.current_seq + 1})</span>
              )}
            </div>
            <button
              onClick={saveCartonTpl}
              disabled={!tplDirty || tplSaving}
              data-testid="save-carton-tpl"
              className="w-full bg-zinc-900 hover:bg-zinc-800 text-white px-3 py-2 text-[11px] uppercase tracking-wider font-mono disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {tplSaving ? "Saving…" : tplDirty ? "Save format for future cartons" : "Saved ✓"}
            </button>
          </div>

          <button onClick={doPrint} disabled={labelData.length === 0} data-testid="print-btn"
            className="w-full bg-orange-600 hover:bg-orange-700 text-white px-3 py-3 text-xs uppercase tracking-wider font-mono flex items-center justify-center gap-2 disabled:opacity-50">
            <Printer size={14} /> Print {totalLabels ? `(${totalLabels} labels)` : ""}
          </button>
        </div>

        {/* ---- Preview / Print area ---- */}
        <div className="print:m-0">
          {labelData.length === 0 ? (
            <div className="bg-white border border-dashed border-zinc-300 p-10 text-center text-sm text-zinc-500 font-mono print:hidden">
              Pick cartons or type text on the left, then click Load. Labels preview will appear here.
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 print:gap-0" data-testid="labels-preview">
              {pages.map((page, pi) => (
                <div key={pi} data-page-card
                  className="bg-white flex flex-row flex-wrap"
                  style={{ width: `${preset.pageW}mm`, height: `${preset.pageH}mm`, gap: "4mm", padding: preset.key === "a4grid" ? "5mm" : "0" }}>
                  {page.map((d, li) => (
                    d.__textOnly ? (
                      <TextOnlyTile key={li} text={d.__text} preset={preset}
                        fontFamily={family.css} fontScale={scale.mult}
                        showCompany={fieldSet.has("company")} />
                    ) : (
                      <LabelTile key={li} d={d} preset={preset}
                        fontFamily={family.css} fontScale={scale.mult}
                        fieldSet={fieldSet} customText={customText}
                        fieldSizes={fieldSizes} dcNetWeight={dcNetWeight} />
                    )
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        @media print {
          @page { size: ${preset.pageW}mm ${preset.pageH}mm; margin: 0; }
          html, body { margin: 0 !important; padding: 0 !important; background: white !important; }
          [data-testid="custom-labels-page"] > div { display: block !important; }
          [data-testid="custom-labels-controls"] { display: none !important; }
          [data-page-card] {
            page-break-after: always; page-break-inside: avoid;
            break-after: page; break-inside: avoid;
            margin: 0 !important;
            display: flex !important; flex-direction: row !important; flex-wrap: wrap !important;
          }
          [data-page-card]:last-child { page-break-after: auto; break-after: auto; }
        }
      `}</style>
    </div>
  );
}

/**
 * Minimal tile for the "Text Only" mode — no carton data at all. Renders
 * the company name (if the Company field is enabled) plus the user's text
 * scaled up to fill the label. Ideal for FRAGILE / HANDLE WITH CARE stickers.
 */
function TextOnlyTile({ text, preset, fontFamily, fontScale, showCompany }) {
  let tileW = preset.pageW;
  let tileH = preset.pageH;
  if (preset.key === "104x50") { tileW = 50; tileH = 50; }
  else if (preset.key === "a4grid") { tileW = 100; tileH = 40; }

  // Bigger base than normal (36px) since the label is just text.
  const bigPx = `${(36 * fontScale).toFixed(2)}px`;
  const smallPx = `${(7 * fontScale).toFixed(2)}px`;

  return (
    <div
      data-label-card
      data-testid={`text-label-${(text || "").slice(0, 12)}`}
      className="bg-white border-2 border-zinc-950 text-zinc-950 print:border-2 print:shadow-none shadow-md flex flex-col items-center justify-center text-center"
      style={{ width: `${tileW}mm`, height: `${tileH}mm`, padding: "3mm", boxSizing: "border-box", fontFamily }}
    >
      {showCompany && (
        <div className="font-mono uppercase tracking-widest text-zinc-500 leading-none mb-2" style={{ fontSize: smallPx }}>
          {"Wellpack Polyplast"}
        </div>
      )}
      <div className="font-bold uppercase tracking-tight leading-tight break-words w-full" style={{ fontSize: bigPx }}>
        {text}
      </div>
    </div>
  );
}
